import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { AlertTriangle, Link2, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  DataTable,
  type DataTableColumn,
} from '@/admin/components/data-table'
import {
  useCreateExceptionActionBinding,
  useCreateExceptionReason,
  useDeleteExceptionActionBinding,
  useDeleteExceptionReason,
  useExceptionActionBindings,
  useExceptionActionCatalog,
  useExceptionReasons,
  useUpdateExceptionActionBinding,
} from '@/shared/hooks/use-workflows'
import { usePermissions } from '@/admin/hooks/use-permissions'
import type {
  ExceptionActionBinding,
  ExceptionActionCatalogEntry,
  ExceptionReason,
} from '@/shared/lib/workflows/types'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Badge } from '@/shared/components/ui/badge'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { useForm, type FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

export function ExceptionsTab() {
  const { can } = usePermissions()
  const { data: reasons = [], isLoading: reasonsLoading } = useExceptionReasons()
  const { data: bindings = [], isLoading: bindingsLoading } =
    useExceptionActionBindings()
  const { data: actionCatalog = [] } = useExceptionActionCatalog()
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [bindingDialogOpen, setBindingDialogOpen] = useState(false)
  const [editingBinding, setEditingBinding] =
    useState<ExceptionActionBinding | null>(null)

  const deleteReason = useDeleteExceptionReason()
  const deleteBinding = useDeleteExceptionActionBinding()
  const canManageExceptions = can('exceptions.manage')
  const availableActions = useMemo(() => {
    return actionCatalog.filter((action) => {
      return !bindings.some((binding) => {
        if (
          binding.workflow_action_uuid &&
          binding.workflow_action_uuid === action.uuid
        ) {
          return true
        }
        return (
          binding.action_key === action.key &&
          binding.resource_type === action.resource_type &&
          binding.workflow_key === action.workflow_key
        )
      })
    })
  }, [actionCatalog, bindings])

  const reasonColumns = useMemo<DataTableColumn<ExceptionReason>[]>(
    () => [
      {
        id: 'title',
        header: 'Exception',
        cell: (row) => (
          <div className="min-w-0">
            <p className="text-[13px] text-[var(--ink)]">{row.title}</p>
            <p className="mt-1 font-mono text-[10px] text-[var(--ink-3)]">
              {row.key}
            </p>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => (
          <span className="line-clamp-2 text-[12px] text-[var(--ink-2)]">
            {row.description || 'No description'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <Badge variant={row.is_active ? 'secondary' : 'outline'}>
            {row.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            disabled={deleteReason.isPending}
            className={canManageExceptions ? undefined : 'hidden'}
            onClick={(event) => {
              event.stopPropagation()
              deleteReason.mutate(row.uuid, {
                onSuccess: () => toast.success('Exception deleted'),
                onError: (err) => toastApiError(err, 'Failed to delete exception'),
              })
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        ),
      },
    ],
    [canManageExceptions, deleteReason],
  )

  const bindingColumns = useMemo<DataTableColumn<ExceptionActionBinding>[]>(
    () => [
      {
        id: 'action',
        header: 'Action',
        cell: (row) => (
          <div className="min-w-0">
            <p className="text-[13px] text-[var(--ink)]">
              {row.action_label || row.action_key}
            </p>
            <p className="mt-1 font-mono text-[10px] text-[var(--ink-3)]">
              {row.action_key}
            </p>
          </div>
        ),
      },
      {
        id: 'workflow',
        header: 'Workflow',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-2)]">
            {row.workflow_label || row.resource_type || 'Any workflow'}
          </span>
        ),
      },
      {
        id: 'suggested',
        header: 'Default exception',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-2)]">
            {row.suggested_exception_title || 'None'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <div
            className={
              canManageExceptions ? 'flex items-center justify-end gap-1' : 'hidden'
            }
          >
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={(event) => {
                event.stopPropagation()
                setEditingBinding(row)
                setBindingDialogOpen(true)
              }}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={deleteBinding.isPending}
              onClick={(event) => {
                event.stopPropagation()
                deleteBinding.mutate(row.uuid, {
                  onSuccess: () => toast.success('Default exception removed'),
                  onError: (err) =>
                    toastApiError(err, 'Failed to remove default exception'),
                })
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [canManageExceptions, deleteBinding],
  )

  return (
    <div className="mt-6 flex flex-col gap-8">
      <section>
        <SectionHeader
          eyebrow="Exception catalogue"
          title="Exceptions"
          description="Create reusable exception reasons that operators select when resolving workflow issues."
          action={
            <Button
              size="sm"
              onClick={() => setReasonDialogOpen(true)}
              disabled={!canManageExceptions}
            >
              <Plus className="h-3.5 w-3.5" />
              Add exception
            </Button>
          }
        />
        <DataTable<ExceptionReason>
          tableId="exception-reasons"
          columns={reasonColumns}
          data={reasons}
          totalCount={reasons.length}
          isLoading={reasonsLoading}
          emptyStateNode={
            <EmptyState
              icon={AlertTriangle}
              title="No exceptions"
              description="Create the first exception reason so it can be suggested when resolving issues."
            />
          }
        />
      </section>

      <section>
        <SectionHeader
          eyebrow="Default exceptions"
          title="Issue resolution defaults"
          description="Choose which exception reason is pre-selected when an operator resolves each issue type. Every issue resolution requires an exception."
          action={
            <Button
              size="sm"
              onClick={() => {
                setEditingBinding(null)
                setBindingDialogOpen(true)
              }}
              disabled={!canManageExceptions}
            >
              <Link2 className="h-3.5 w-3.5" />
              Add default
            </Button>
          }
        />
        <DataTable<ExceptionActionBinding>
          tableId="exception-action-bindings"
          columns={bindingColumns}
          data={bindings}
          totalCount={bindings.length}
          isLoading={bindingsLoading}
          emptyStateNode={
            <EmptyState
              icon={Link2}
              title="No defaults configured"
              description="Add a default to pre-select an exception reason when operators resolve a specific issue type."
            />
          }
        />
      </section>

      <CreateExceptionReasonDialog
        open={reasonDialogOpen}
        onOpenChange={setReasonDialogOpen}
        canManage={canManageExceptions}
      />
      <CreateExceptionBindingDialog
        open={bindingDialogOpen}
        onOpenChange={(open) => {
          setBindingDialogOpen(open)
          if (!open) setEditingBinding(null)
        }}
        reasons={reasons}
        actions={
          editingBinding
            ? [
                ...availableActions,
                ...actionCatalog.filter((action) =>
                  actionMatchesBinding(action, editingBinding),
                ),
              ]
            : availableActions
        }
        canManage={canManageExceptions}
        binding={editingBinding}
      />
    </div>
  )
}

function SectionHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string
  title: string
  description: string
  action: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="clara-label text-[var(--ink-3)]">{eyebrow}</p>
        <h2 className="mt-1 text-[18px] text-[var(--ink)]">{title}</h2>
        <p className="mt-1 max-w-[720px] text-[12px] leading-relaxed text-[var(--ink-3)]">
          {description}
        </p>
      </div>
      {action}
    </div>
  )
}

function CreateExceptionReasonDialog({
  open,
  onOpenChange,
  canManage,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  canManage: boolean
}) {
  const createReason = useCreateExceptionReason()
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'title',
        label: 'Title',
        value: '',
        required: 'Title is required',
        placeholder: 'e.g. Late registration approved',
      },
      {
        type: 'textarea',
        name: 'description',
        label: 'Description',
        value: '',
        placeholder: 'Explain when this exception should be used.',
      },
    ],
    [],
  )
  const { Form } = useForm(
    { name: 'create-exception-reason-form', submit: 'footer', fields },
    async (values) => {
      try {
        if (!canManage) {
          toast.error('You need Exceptions manage permission to create exceptions.')
          return
        }
        await createReason.mutateAsync({
          title: String(values.title ?? '').trim(),
          description: String(values.description ?? '').trim(),
        })
        toast.success('Exception created')
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to create exception')
      }
    },
    [canManage, createReason, onOpenChange],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create exception</DialogTitle>
          <DialogDescription>
            The key is generated automatically from the title and shown after save.
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-exception-reason-form"
            disabled={createReason.isPending || !canManage}
          >
            {createReason.isPending ? 'Creating...' : 'Create exception'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CreateExceptionBindingDialog({
  open,
  onOpenChange,
  reasons,
  actions,
  canManage,
  binding,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  reasons: ExceptionReason[]
  actions: ExceptionActionCatalogEntry[]
  canManage: boolean
  binding?: ExceptionActionBinding | null
}) {
  const createBinding = useCreateExceptionActionBinding()
  const updateBinding = useUpdateExceptionActionBinding()
  const selectedActionUuid = binding?.workflow_action_uuid || ''
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'select',
        name: 'workflow_action',
        label: 'Issue resolution action',
        value: selectedActionUuid,
        required: 'Select an issue resolution action',
        options: actions.map((action) => {
          const issuePrefix =
            action.issue_labels?.length > 0
              ? action.issue_labels.join(', ')
              : action.workflow_label
          return {
            value: action.uuid,
            label: `${issuePrefix} - ${action.label || action.key}`,
          }
        }),
      },
      {
        type: 'select',
        name: 'suggested_exception',
        label: 'Default exception',
        value: binding?.suggested_exception_key || '',
        required: 'Select a default exception',
        options: [
          ...reasons.map((reason) => ({
            value: reason.key,
            label: reason.title,
          })),
        ],
      },
    ],
    [actions, binding?.suggested_exception_key, reasons, selectedActionUuid],
  )
  const { Form } = useForm(
    { name: 'create-exception-binding-form', submit: 'footer', fields },
    async (values) => {
      const action = actions.find((item) => item.uuid === values.workflow_action)
      if (!canManage) {
        toast.error('You need Exceptions manage permission to configure default exceptions.')
        return
      }
      if (!action) {
        toast.error('Select an issue resolution action')
        return
      }
      try {
        const payload = {
          workflow_action: action.uuid,
          action_key: action.key,
          action_label: action.label,
          resource_type: action.resource_type,
          suggested_exception: String(values.suggested_exception || '') || null,
        }
        if (binding) {
          await updateBinding.mutateAsync({ uuid: binding.uuid, payload })
          toast.success('Default exception updated')
        } else {
          await createBinding.mutateAsync(payload)
          toast.success('Default exception added')
        }
        onOpenChange(false)
      } catch (err) {
        toastApiError(
          err,
          binding ? 'Failed to update default exception' : 'Failed to add default exception',
        )
      }
    },
    [actions, binding, canManage, createBinding, onOpenChange, updateBinding],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{binding ? 'Edit default exception' : 'Add default exception'}</DialogTitle>
          <DialogDescription>
            Choose which exception reason is pre-selected in the resolve modal for this issue resolution action.
          </DialogDescription>
        </DialogHeader>
        <Form className="flex flex-col gap-3" />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-exception-binding-form"
            disabled={createBinding.isPending || updateBinding.isPending || !canManage}
          >
            {createBinding.isPending || updateBinding.isPending
              ? 'Saving...'
              : binding
                ? 'Save changes'
                : 'Add default'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function actionMatchesBinding(
  action: ExceptionActionCatalogEntry,
  binding: ExceptionActionBinding,
): boolean {
  if (binding.workflow_action_uuid && binding.workflow_action_uuid === action.uuid) {
    return true
  }
  return (
    binding.action_key === action.key &&
    binding.resource_type === action.resource_type &&
    binding.workflow_key === action.workflow_key
  )
}
